import { useState } from "react";
import { parsePatientJsonField } from "../Support/validators/helper/patientFields";
import { Badge } from "../ui/Badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/Table";
import VisitFeedbackModal, {
  CodeWithRationale,
  OverrideRationaleModal,
  RatingWithComment,
} from "./VisitFeedbackModal";
import {
  formatCodeOrDash,
  getEandMComparison,
  getPatientStarRating,
} from "./patientVisitFields";

const STAT_HEAD_CLASS = "h-auto w-1/6 px-0 pr-4 pb-1 align-bottom text-xs font-normal";
const STAT_CELL_CLASS = "p-0 pr-4 pt-1 align-top text-sm font-semibold leading-5";

export default function ShowNoteDetails({ patientRecord, closeNote }) {
  const [showFeedback, setShowFeedback] = useState(false);
  const [showRationale, setShowRationale] = useState(false);
  const note = patientRecord?.note;
  const mrn = patientRecord?.mrn;
  const patient = patientRecord?.description;
  const icd10 = parsePatientJsonField(patientRecord?.icd10_codes_v2);
  const codes = icd10?.selected_codes?.codes || [];
  const primaryCodes = codes.filter((code) => code.codeType === "Primary");
  const secondaryCodes = codes.filter((code) => code.codeType === "Secondary");
  const eAndM = getEandMComparison(patientRecord);
  const { rating, additionalFeedback } = getPatientStarRating(patientRecord?.user_feedback);

  if (!note) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 w-[50%] max-w-xl">
          <div className="flex justify-between items-center mb-4">
            <div className="flex flex-col">
              <h2 className="text-2xl font-bold text-ktx-navy">Patient Information</h2>
              <h3 className="text-lg text-ktx-navy">MRN: {mrn}</h3>
              <h3 className="text-lg text-ktx-navy">{patient}</h3>
            </div>
            <button onClick={closeNote} className="text-gray-500 hover:text-ktx-navy">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="text-center py-8 text-gray-500 italic">
            No note available for this patient yet
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[80%] max-w-3xl">
        <div className="flex justify-between items-center mb-4">
          <div className="flex flex-col">
            <h2 className="text-2xl font-bold text-ktx-navy">Note Details for {patient}</h2>
            <h3 className="text-lg text-ktx-navy">MRN: {mrn}</h3>
          </div>
          <button onClick={closeNote} className="text-gray-500 hover:text-ktx-navy">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="mb-4">
          <Table className="table-fixed">
            <TableHeader className="[&_tr]:border-0">
              <TableRow className="border-0 hover:bg-transparent">
                <TableHead className={STAT_HEAD_CLASS}>Primary codes</TableHead>
                <TableHead className={STAT_HEAD_CLASS}>Secondary codes</TableHead>
                <TableHead className={STAT_HEAD_CLASS}>E&M selected?</TableHead>
                <TableHead className={STAT_HEAD_CLASS}>Final E&M</TableHead>
                <TableHead className={STAT_HEAD_CLASS}>Knowtex E&M</TableHead>
                <TableHead className={`${STAT_HEAD_CLASS} pr-0`}>Star Rating</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow className="hover:bg-transparent">
                <TableCell className={`${STAT_CELL_CLASS} text-ktx-purple`}>
                  {primaryCodes.length}
                </TableCell>
                <TableCell className={`${STAT_CELL_CLASS} text-ktx-navy`}>
                  {secondaryCodes.length}
                </TableCell>
                <TableCell className={STAT_CELL_CLASS}>
                  <Badge
                    variant="outline"
                    className={eAndM.providerSelected
                      ? "border-purple-200 bg-purple-50 text-ktx-purple"
                      : "border-gray-200 bg-gray-50 text-gray-600"}
                  >
                    {eAndM.providerSelected ? "Yes" : "No"}
                  </Badge>
                </TableCell>
                <TableCell className={`${STAT_CELL_CLASS} text-ktx-navy`}>
                  <CodeWithRationale
                    code={eAndM.finalCode}
                    rationale={eAndM.overrideRationale}
                    onOpenRationale={() => setShowRationale(true)}
                    stacked
                  />
                </TableCell>
                <TableCell className={`${STAT_CELL_CLASS} text-ktx-navy`}>
                  {formatCodeOrDash(eAndM.knowtexCode)}
                </TableCell>
                <TableCell className={`${STAT_CELL_CLASS} pr-0 text-ktx-navy`}>
                  <RatingWithComment
                    rating={rating}
                    additionalFeedback={additionalFeedback}
                    onOpenComment={() => setShowFeedback(true)}
                    stacked
                  />
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>

        <div className="flex flex-col">
          <h3 className="text-sm font-semibold text-ktx-navy mb-1">Note</h3>
          <div className="max-h-[30vh] overflow-y-auto rounded-md bg-gray-50 px-3 py-2 text-sm text-ktx-navy whitespace-pre-wrap">
            {note}
          </div>

          <h3 className="text-sm font-semibold mt-4 mb-2 text-ktx-navy">ICD-10 Codes</h3>
          <div className="max-h-[30vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-x-6">
              {primaryCodes.length > 0 && (
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">Primary</h4>
                  {primaryCodes.map((code, index) => (
                    <div key={index} className="py-1.5 border-b border-gray-100 last:border-0">
                      <div className="text-sm font-semibold text-ktx-purple">{code.code}</div>
                      <div className="text-xs text-gray-600">{code.name}</div>
                    </div>
                  ))}
                </div>
              )}

              {secondaryCodes.length > 0 && (
                <div>
                  <h4 className="text-xs text-gray-500 mb-1">Secondary</h4>
                  {secondaryCodes.map((code, index) => (
                    <div key={index} className="py-1.5 border-b border-gray-100 last:border-0">
                      <div className="text-sm font-semibold text-ktx-navy">{code.code}</div>
                      <div className="text-xs text-gray-600">{code.name}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <VisitFeedbackModal
        open={showFeedback}
        onOpenChange={setShowFeedback}
        patientName={patient}
        mrn={mrn}
        rating={rating}
        additionalFeedback={additionalFeedback}
      />
      <OverrideRationaleModal
        open={showRationale}
        onOpenChange={setShowRationale}
        patientName={patient}
        mrn={mrn}
        finalCode={eAndM.finalCode}
        knowtexCode={eAndM.knowtexCode}
        overrideRationale={eAndM.overrideRationale}
      />
    </div>
  );
}
